import types from "../types";
import { FACILITY_CODES } from "../../constants";

// initial state
const state = {
  ids: [],
  entities: {},
};

// getters
const getters = {
  [types.getters.postArrangements]: (state) =>
    state.ids.map((id) => state.entities[id]),
  [types.getters.postArrangement]: (state) => (postArrangementID) =>
    state.entities[postArrangementID],
  [types.getters.postArrangementByContactID]: (state) => (contactID) => {
    return Object.values(state.entities).filter(
      (postArrangement) => postArrangement.contactID === contactID,
    );
  },
  [types.getters.postArrangementByContactIDAndCategory]:
    (state) => (contactID, category) => {
      return Object.values(state.entities).find(
        (postArrangement) =>
          postArrangement.contactID === contactID &&
          postArrangement.category === category,
      );
    },
};

// actions
const actions = {
  [types.actions.updatePostArrangementField](
    { commit, getters, state },
    { postArrangementID, fieldName, value, triggerReset = true },
  ) {
    commit(types.mutations.updatePostArrangementField, {
      postArrangementID,
      fieldName,
      value,
    });

    if (fieldName === "postArrangementAction") {
      const postArrangementPortToLocation = state.entities[postArrangementID];

      if (postArrangementPortToLocation.category !== "Port to location") {
        console.warn(
          `Expected postArrangement's category to be 'Port to location' but is ${postArrangementPortToLocation.category}`,
        );
        return;
      }

      const postArrangementAccommodation = getters[
        types.getters.postArrangementByContactIDAndCategory
      ](postArrangementPortToLocation.contactID, "Accommodation");
      const postArrangementFlight = getters[
        types.getters.postArrangementByContactIDAndCategory
      ](postArrangementPortToLocation.contactID, "Flight");

      switch (value) {
        case "airport":
          commit(types.mutations.updatePostArrangementField, {
            postArrangementID: postArrangementFlight.postArrangementID,
            fieldName: "notTakingAFlight",
            value: false,
          });
          commit(types.mutations.resetPostArrangementAccommodationDetail, {
            postArrangementID: postArrangementAccommodation.postArrangementID,
          });
          if (triggerReset) {
            resetPostArrangementPortToLocation(postArrangementPortToLocation);
          }
          break;
        case "hotel":
          commit(types.mutations.resetPostArrangementFlightDetail, {
            postArrangementID: postArrangementFlight.postArrangementID,
          });
          if (triggerReset) {
            resetPostArrangementPortToLocation(postArrangementPortToLocation);
          }
          break;
        case "sharedTransfer":
          commit(types.mutations.resetPostArrangementAccommodationDetail, {
            postArrangementID: postArrangementAccommodation.postArrangementID,
          });
          commit(types.mutations.updatePostArrangementField, {
            postArrangementID: postArrangementPortToLocation.postArrangementID,
            fieldName: "typeOfArrangement",
            value: "AE Shared transfer",
          });
          break;
        default:
          break;
      }

      function resetPostArrangementPortToLocation(
        postArrangementPortToLocation,
      ) {
        if (
          postArrangementPortToLocation.typeOfArrangement ===
          "AE Shared transfer"
        ) {
          commit(types.mutations.resetPostArrangementPortToLocationDetail, {
            postArrangementID: postArrangementPortToLocation.postArrangementID,
          });
        }
      }
    }
  },
};

// mutations
const mutations = {
  [types.mutations.setPostArrangements](state, { postArrangements, booking }) {
    state.ids = postArrangements.map(
      (postArrangement) => postArrangement.postArrangementID,
    );
    state.entities = postArrangements.reduce((accumulate, current) => {
      if (current.category === "Port to location") {
        // For booking that isAmazonCruise or isToPhnomPenh or for Aqua Blu, there is no 'sharedTransfer' option in the select list.
        if (
          !booking.isAmazonCruise &&
          !booking.isToPhnomPenh &&
          booking.facilityCode !== FACILITY_CODES.AquaBlu &&
          current.typeOfArrangement === "AE Shared transfer"
        ) {
          current.postArrangementAction = "sharedTransfer";
        } else {
          const postArrangementFlight = postArrangements.find(
            (postArrangement) =>
              postArrangement.contactID === current.contactID &&
              postArrangement.category === "Flight",
          );

          // assume action is airport if notTakingAFlight is false
          if (
            postArrangementFlight &&
            !postArrangementFlight.notTakingAFlight
          ) {
            current.postArrangementAction = "airport";
          }
          // defaults to hotel
          else {
            current.postArrangementAction = "hotel";
          }
        }
      }
      accumulate[current.postArrangementID] = current;
      return accumulate;
    }, {});
  },
  [types.mutations.updatePostArrangementField](
    state,
    { postArrangementID, fieldName, value },
  ) {
    state.entities[postArrangementID][fieldName] = value;
  },
  [types.mutations.resetPostArrangementAccommodationDetail](
    state,
    { postArrangementID },
  ) {
    if (state.entities[postArrangementID].category !== "Accommodation") {
      console.warn(
        `Expected category to be 'Accommodation' but is ${state.entities[postArrangementID].category}, postArrangementID: ${state.entities[postArrangementID].postArrangementID}`,
      );
      return;
    }

    state.entities[postArrangementID].accommodationName = "";
  },
  [types.mutations.resetPostArrangementFlightDetail](
    state,
    { postArrangementID },
  ) {
    if (state.entities[postArrangementID].category !== "Flight") {
      console.warn(
        `Expected category to be 'Flight' but is ${state.entities[postArrangementID].category}, postArrangementID: ${state.entities[postArrangementID].postArrangementID}`,
      );
      return;
    }

    state.entities[postArrangementID].notTakingAFlight = true;
    state.entities[postArrangementID].takingRecommendedFlight = false;
    state.entities[postArrangementID].flightNumber = "";
    state.entities[postArrangementID].departureDate = null;
    state.entities[postArrangementID].departureTime = null;
  },
  [types.mutations.resetPostArrangementPortToLocationDetail](
    state,
    { postArrangementID },
  ) {
    if (state.entities[postArrangementID].category !== "Port to location") {
      console.warn(
        `Expected category to be 'Port to location' but is ${state.entities[postArrangementID].category}, postArrangementID: ${state.entities[postArrangementID].postArrangementID}`,
      );
      return;
    }

    state.entities[postArrangementID].typeOfArrangement = "";
    state.entities[postArrangementID].nameOfPerson = "";
    state.entities[postArrangementID].contactNo = "";
  },
  [types.mutations.copyPostArrangement](
    state,
    { postArrangement, postArrangementToCopyFrom },
  ) {
    state.entities[postArrangement.postArrangementID] = {
      ...postArrangementToCopyFrom,
      postArrangementID: postArrangement.postArrangementID,
      contactID: postArrangement.contactID,
      title: postArrangement.title,
    };
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/postArrangement.js